import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../../supabaseClient';
import { usePermissions } from '../../utils/permissions';
import {
  Box, Container, Typography, Button, TextField, Dialog, DialogTitle, DialogContent,
  DialogActions, IconButton, Alert, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TablePagination, Fab, Card, CardContent, Snackbar, useTheme,
  alpha, Fade, LinearProgress, InputAdornment, Grid, Divider, Tooltip, Chip,
  FormControl, InputLabel, Select, MenuItem
} from '@mui/material';
import {
  Add as AddIcon, Delete as DeleteIcon, Edit as EditIcon,
  Close as CloseIcon, Search as SearchIcon, Output as OutputIcon,
  Check as CheckIcon, Cancel as CancelIcon,
  Verified as VerifiedIcon, CheckCircle as CheckCircleIcon,
  Visibility as VisibilityIcon, AttachFile as AttachFileIcon,
  CloudUpload as CloudUploadIcon, Description as DescriptionIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { jsPDF } from 'jspdf';

const UNIT_TYPES = [
  { id: 'unit', name: 'Unit', plural: 'Units' },
  { id: 'case', name: 'Case', plural: 'Cases' },
];

// Helper function to get the correct unit label based on quantity
const getUnitLabel = (unitTypeId, quantity) => {
  const unitType = UNIT_TYPES.find(u => u.id === unitTypeId);
  if (!unitType) return '';
  return quantity > 1 ? unitType.plural : unitType.name;
};

const ExportsPage = () => {
  const { companyId } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const { canViewExports, canEditExports, canDeleteExports, canApproveExports } = usePermissions();
  
  // State management
  const [exports, setExports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [companyName, setCompanyName] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [exportToDelete, setExportToDelete] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  
  // Approval dialog states
  const [approvalDialogOpen, setApprovalDialogOpen] = useState(false);
  const [approvalExport, setApprovalExport] = useState(null);
  const [signatureName, setSignatureName] = useState('');
  const [approvalNotes, setApprovalNotes] = useState('');
  
  // View dialog states
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [viewExport, setViewExport] = useState(null);
  
  // Search state
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredExports, setFilteredExports] = useState([]);
  
  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  // Form data
  const [formData, setFormData] = useState({
    id: '',
    serial_number: '',
    date_of_dispatch: dayjs(),
    po_number: '',
    customer_name: '',
    invoice_number: '',
    invoice_date: null,
    delivery_method: '',
    driver_name: '',
    delivery_location: '',
    remarks: '',
    invoice_file_path: '',
    invoice_file_name: '',
    invoice_file_type: '',
    invoice_file_size: null
  });

  // Line items state
  const [lineItems, setLineItems] = useState([
    {
      product_name: '',
      quantity: '',
      unit_type: 'unit',
      batch_no: '',
      lot_no: '',
      mfg_date: null,
      exp_date: null
    }
  ]);

  // Invoice file state
  const [invoiceFile, setInvoiceFile] = useState(null);
  const [uploadingInvoice, setUploadingInvoice] = useState(false);

  // Autofill state
  const [poListDialogOpen, setPoListDialogOpen] = useState(false);
  const [availablePOs, setAvailablePOs] = useState([]);
  const [loadingPOs, setLoadingPOs] = useState(false);
  const [poSearchTerm, setPoSearchTerm] = useState('');
  const [loadingAutofill, setLoadingAutofill] = useState(false);

  // Invoice select state
  const [companyData, setCompanyData] = useState(null);
  const [invoiceSelectDialogOpen, setInvoiceSelectDialogOpen] = useState(false);
  const [availableInvoices, setAvailableInvoices] = useState([]);
  const [loadingInvoices, setLoadingInvoices] = useState(false);
  const [invoiceSearchTerm, setInvoiceSearchTerm] = useState('');
  const [generatingInvoice, setGeneratingInvoice] = useState(false);

  const fetchCompanyData = async () => {
    try {
      const { data, error } = await supabase
        .from('companies')
        .select('*')
        .eq('id', companyId)
        .single();
      if (error) throw error;
      setCompanyName(data?.name || 'Company');
      setCompanyData(data);
    } catch (err) {
      console.error('Error fetching company:', err);
      setCompanyName('Company Name');
    }
  };

  const handleOpenInvoiceSelectDialog = async () => {
    setInvoiceSelectDialogOpen(true);
    setLoadingInvoices(true);
    try {
      const { data, error } = await supabase
        .from('invoices')
        .select('id, invoice_number, customer_name, invoice_date, status')
        .eq('company_id', companyId)
        .order('invoice_date', { ascending: false });
      if (error) throw error;
      setAvailableInvoices(data || []);
    } catch (err) {
      console.error('Error fetching invoices:', err);
      setSnackbar({ open: true, message: 'Error loading invoices', severity: 'error' });
    } finally {
      setLoadingInvoices(false);
    }
  };

  const handleSelectInvoiceRecord = async (invoiceId) => {
    setInvoiceSelectDialogOpen(false);
    setGeneratingInvoice(true);
    try {
      const { data: inv, error } = await supabase
        .from('invoices')
        .select('*, invoice_items (*)')
        .eq('id', invoiceId)
        .single();
      if (error) throw error;

      // Fetch customer for phone/email
      let customer = null;
      if (inv.customer_id) {
        const { data: custData } = await supabase
          .from('customers')
          .select('name, company, email, phone')
          .eq('id', inv.customer_id)
          .single();
        customer = custData || null;
      }

      const items = (inv.invoice_items || []).filter(i => i.product_id);

      const doc = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'letter' });

      // Load script font for signature name
      let scriptFontLoaded = false;
      try {
        const fontResp = await fetch('https://fonts.gstatic.com/s/greatvibes/v19/RWmMoKWR9v4ksMfaWd_JN9XKlTc.ttf');
        if (fontResp.ok) {
          const fontBuf = await fontResp.arrayBuffer();
          const fontB64 = btoa(String.fromCharCode(...new Uint8Array(fontBuf)));
          doc.addFileToVFS('GreatVibes.ttf', fontB64);
          doc.addFont('GreatVibes.ttf', 'GreatVibes', 'normal');
          scriptFontLoaded = true;
        }
      } catch (_) { /* fallback to times italic */ }
      const PW = doc.internal.pageSize.getWidth();
      const PH = doc.internal.pageSize.getHeight();
      const M = 40;
      let y = M;

      const fmtD = (d) => {
        if (!d) return 'N/A';
        const s = String(d).split('T')[0];
        const p = s.split('-');
        return p.length === 3 ? `${p[1]}/${p[2]}/${p[0]}` : s;
      };
      const fmtC = (n) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n || 0);

      // ── INVOICE title (left) ──
      doc.setFontSize(28); doc.setFont('helvetica', 'normal'); doc.setTextColor(0, 0, 0);
      doc.text('INVOICE', M, y);

      // ── Company info (right) ──
      let cy = M;
      doc.setFontSize(12); doc.setFont('helvetica', 'bold');
      doc.text(companyData?.name || 'Your Company Name', PW - M, cy, { align: 'right' });
      cy += 15; doc.setFont('helvetica', 'normal'); doc.setFontSize(9); doc.setTextColor(80, 80, 80);
      const coLines = [
        companyData?.address,
        [companyData?.city, companyData?.state, companyData?.zip_code].filter(Boolean).join(', '),
        companyData?.country ? ({ 'US': 'United States', 'USA': 'United States', 'CA': 'Canada', 'MX': 'Mexico', 'UK': 'United Kingdom', 'GB': 'United Kingdom' }[companyData.country] || companyData.country) : null,
        companyData?.phone  ? `Phone: ${companyData.phone}`   : null,
        companyData?.email  ? `Email: ${companyData.email}`   : null,
        companyData?.website? `Web: ${companyData.website}`   : null,
      ].filter(Boolean);
      coLines.forEach(l => { doc.text(l, PW - M, cy, { align: 'right' }); cy += 12; });

      // ── Invoice detail lines (left, below title) ──
      y += 20; doc.setFontSize(9); doc.setFont('helvetica', 'normal'); doc.setTextColor(100, 100, 100);
      [
        `Invoice #: ${inv.invoice_number || 'N/A'}`,
        `Date: ${fmtD(inv.invoice_date)}`,
        `Due: ${fmtD(inv.due_date)}`,
        inv.purchase_order_number ? `PI Ref: ${inv.purchase_order_number}` : null,
        inv.bill_of_lading_number ? `BOL Ref: ${inv.bill_of_lading_number}` : null,
      ].filter(Boolean).forEach(l => { doc.text(l, M, y); y += 12; });
      if (inv.status) {
        doc.setFont('helvetica', 'bold'); doc.setTextColor(0, 0, 0);
        doc.text(inv.status.toUpperCase(), M, y); y += 12;
      }
      y = Math.max(y, cy) + 16;

      // ── Divider ──
      doc.setDrawColor(200, 200, 200); doc.setLineWidth(0.5);
      doc.line(M, y, PW - M, y); y += 16;

      // ── Bill To / Ship To ──
      const hasShip = !!(inv.shipping_street || inv.shipping_city);
      doc.setFont('helvetica', 'bold'); doc.setFontSize(9); doc.setTextColor(0, 0, 0);
      doc.text('Bill To:', M, y);
      if (hasShip) doc.text('Ship To:', PW / 2 + 20, y);
      y += 14; doc.setFont('helvetica', 'normal'); doc.setTextColor(40, 40, 40);

      const buildAddrLines = (name, company, phone, email, street, city, state, zip, country) => {
        const lines = [];
        if (name)    lines.push(name);
        if (company) lines.push(company);
        if (phone)   lines.push(`Phone: ${phone}`);
        if (email)   lines.push(`Email: ${email}`);
        const addrParts = [street, [city, state, zip].filter(Boolean).join(', '), country].filter(Boolean);
        if (addrParts.length) {
          lines.push(''); // blank line gap before address
          lines.push('Address:');
          addrParts.forEach(l => lines.push(l));
        }
        return lines;
      };

      const renderLines = (lines, x, startY) => {
        let ly = startY;
        lines.forEach(l => {
          if (l === '') { ly += 6; } // blank line = small gap
          else { doc.text(l, x, ly); ly += 12; }
        });
        return ly;
      };

      const billLines = buildAddrLines(
        inv.customer_name, inv.customer_company || customer?.company,
        customer?.phone, customer?.email,
        inv.billing_street, inv.billing_city, inv.billing_state, inv.billing_zip, inv.billing_country
      );
      const billY = y;
      y = renderLines(billLines, M, y);

      if (hasShip) {
        const shipLines = buildAddrLines(
          inv.customer_name, inv.customer_company || customer?.company,
          customer?.phone, customer?.email,
          inv.shipping_street, inv.shipping_city, inv.shipping_state, inv.shipping_zip, inv.shipping_country
        );
        const shipEndY = renderLines(shipLines, PW / 2 + 20, billY);
        y = Math.max(y, shipEndY);
      }
      y += 20;

      // ── Order Summary ──
      doc.setFont('helvetica', 'bold'); doc.setFontSize(11); doc.setTextColor(0, 0, 0);
      doc.text('Order Summary', M, y); y += 6;
      doc.setDrawColor(0); doc.setLineWidth(0.5);
      doc.line(M, y, PW - M, y); y += 14;

      if (items.length > 0) {
        const CW = [180, 60, 55, 80, 75, 82];
        const TW = PW - M * 2;
        // header
        doc.setFillColor(245, 245, 245);
        doc.rect(M, y, TW, 20, 'F');
        doc.setDrawColor(224, 224, 224);
        doc.rect(M, y, TW, 20, 'S');
        doc.setFont('helvetica', 'bold'); doc.setFontSize(8);
        ['Product','Size','UoM','Quantity','Unit Price','Amount'].forEach((h, i) => {
          const x = M + CW.slice(0,i).reduce((a,b)=>a+b,0) + 5;
          if (i >= 3) doc.text(h, x + CW[i] - 10, y + 14, { align: 'right' });
          else        doc.text(h, x, y + 14);
        });
        y += 20;
        doc.setFont('helvetica', 'normal');
        items.forEach(item => {
          if (y > PH - M - 60) { doc.addPage(); y = M; }
          const qty = parseFloat(item.quantity) || 0;
          const qtyLbl = item.unit_type === 'Unit' ? `${qty} ${qty===1?'Unit':'Units'}` : `${qty} ${qty===1?'Case':'Cases'}`;
          const uom = item.unit_type !== 'Unit' && item.units_per_case ? `${item.units_per_case}U/C` : '-';
          const rowH = 20;
          doc.setDrawColor(224, 224, 224);
          doc.rect(M, y, TW, rowH, 'S');
          const cells = [item.product_name||'—', item.variant_name||'-', uom, qtyLbl, fmtC(item.unit_price), fmtC(item.line_total||item.total_price)];
          cells.forEach((cell, i) => {
            const x = M + CW.slice(0,i).reduce((a,b)=>a+b,0) + 5;
            if (i >= 3) doc.text(cell, x + CW[i] - 10, y + 14, { align: 'right' });
            else        doc.text(cell, x, y + 14);
          });
          y += rowH;
        });
      }
      y += 16;

      // ── Totals ──
      const TX = PW - M - 210;
      const addRow = (label, val, bold) => {
        doc.setFont('helvetica', bold ? 'bold' : 'normal');
        doc.setFontSize(bold ? 10 : 9);
        doc.setTextColor(0,0,0);
        doc.text(label, TX, y); doc.text(val, PW - M, y, { align: 'right' }); y += 14;
      };
      // Compute discount_amount in case it's not stored on the record
      addRow('Subtotal:', fmtC(inv.subtotal));
      if (parseFloat(inv.discount) > 0) {
        const discountLabel = inv.discount_type === 'flat' ? 'Discount:' : `Discount (${inv.discount}%):`;
        const discountAmt = parseFloat(inv.discount_amount) || (inv.discount_type === 'flat' ? parseFloat(inv.discount) : (parseFloat(inv.subtotal) * parseFloat(inv.discount)) / 100) || 0;
        addRow(discountLabel, `-${fmtC(discountAmt)}`);
      }
      if (parseFloat(inv.tax_amount) > 0) addRow(`Tax (${inv.tax_rate}%):`, `+${fmtC(inv.tax_amount)}`);
      if (parseFloat(inv.shipping_fee) > 0) addRow('Shipping:', `+${fmtC(inv.shipping_fee)}`);
      if (parseFloat(inv.handling_fee) > 0) addRow('Handling:', `+${fmtC(inv.handling_fee)}`);
      doc.setDrawColor(0); doc.setLineWidth(1);
      doc.line(TX, y, PW - M, y); y += 12;
      addRow('Total:', fmtC(inv.total_before_credit), true);
      if (parseFloat(inv.credit_applied) > 0) {
        doc.setTextColor(46,125,50); addRow('Credit Applied:', `-${fmtC(inv.credit_applied)}`);
        doc.setTextColor(0,0,0); doc.setLineWidth(0.5);
        doc.line(TX, y, PW - M, y); y += 12;
        addRow('Amount Due:', fmtC(inv.amount_due), true);
      }
      y += 16;

      // ── Payment Terms ──
      if (inv.payment_terms || inv.payment_method || parseFloat(inv.late_fee_rate) > 0) {
        if (y > PH - M - 60) { doc.addPage(); y = M; }
        doc.setFont('helvetica','bold'); doc.setFontSize(9); doc.setTextColor(0,0,0);
        doc.text('Payment Terms:', M, y); y += 14;
        doc.setFont('helvetica','normal');
        if (inv.payment_terms) { doc.text(`Payment Due: ${inv.payment_terms}`, M, y); y += 12; }
        if (inv.payment_method) { doc.text(`Preferred Method: ${inv.payment_method}`, M, y); y += 12; }
        if (parseFloat(inv.early_payment_discount) > 0) { doc.text(`Early Payment: ${inv.early_payment_discount}% discount if paid within ${inv.early_payment_days||0} days`, M, y); y += 12; }
        if (parseFloat(inv.late_fee_rate) > 0) { doc.text(`Late Fee: ${inv.late_fee_rate}% per month on overdue balances`, M, y); y += 12; }
        y += 8;
      }

      // ── Notes ──
      if (inv.notes) {
        if (y > PH - M - 50) { doc.addPage(); y = M; }
        doc.setTextColor(0,0,0); doc.setFont('helvetica','bold'); doc.setFontSize(9); doc.text('Notes:', M, y); y += 14;
        doc.setFont('helvetica','normal'); doc.setTextColor(0,0,0);
        const nl = doc.splitTextToSize(inv.notes, PW - M * 2);
        doc.text(nl, M, y); y += nl.length * 11 + 10;
      }

      // ── Special Instructions ──
      if (inv.special_instructions) {
        if (y > PH - M - 50) { doc.addPage(); y = M; }
        doc.setTextColor(0,0,0); doc.setFont('helvetica','bold'); doc.setFontSize(9); doc.text('Special Instructions:', M, y); y += 14;
        doc.setFont('helvetica','normal'); doc.setTextColor(0,0,0);
        const sl = doc.splitTextToSize(inv.special_instructions, PW - M * 2);
        doc.text(sl, M, y); y += sl.length * 11 + 10;
      }

      // ── Terms & Conditions box ──
      if (inv.terms_and_conditions) {
        if (y > PH - M - 60) { doc.addPage(); y = M; }
        y += 10;
        const pad = 10; const lh = 11;
        doc.setFontSize(8);
        const tLines = doc.splitTextToSize(inv.terms_and_conditions, PW - M * 2 - pad * 2);
        if (y + 50 > PH - M) { doc.addPage(); y = M; }
        let bsy = y;
        doc.setFont('helvetica','bold'); doc.setFontSize(9);
        doc.text('Terms & Conditions', M + pad, y + 13); y += 22;
        doc.setFont('helvetica','normal'); doc.setFontSize(8);
        let pbsy = bsy;
        tLines.forEach(line => {
          if (y + lh > PH - M) {
            doc.setDrawColor(0); doc.rect(M, pbsy, PW - M * 2, y - pbsy + pad, 'S');
            doc.addPage(); y = M; pbsy = y;
            doc.setFont('helvetica','bold'); doc.setFontSize(9);
            doc.text('Terms & Conditions (continued)', M + pad, y + 13); y += 22;
            doc.setFont('helvetica','normal'); doc.setFontSize(8);
          }
          doc.text(line, M + pad, y); y += lh;
        });
        doc.setDrawColor(0); doc.rect(M, pbsy, PW - M * 2, y - pbsy + pad, 'S');
        y += pad + 10;
      }

      // ── Footer ──
      y += 20;
      if (inv.prepared_by?.name) {
        // Build the right-side prepared_by block first, then place "Thank you" at the bottom
        let ry = y;
        doc.setFont('helvetica','bold'); doc.setFontSize(9); doc.setTextColor(0,0,0);
        doc.text('Prepared by:', PW - M, ry, { align: 'right' }); ry += 20;
        if (scriptFontLoaded) {
          doc.setFont('GreatVibes', 'normal');
        } else {
          doc.setFont('times', 'italic');
        }
        doc.setFontSize(22); doc.setTextColor(0,0,0);
        doc.text(inv.prepared_by.name, PW - M, ry, { align: 'right' }); ry += 22;
        doc.setFont('helvetica','normal'); doc.setFontSize(8); doc.setTextColor(100,100,100);
        doc.text(inv.prepared_by.position || 'Sales Representative', PW - M, ry, { align: 'right' }); ry += 12;
        if (inv.prepared_by.date) {
          doc.text(fmtD(inv.prepared_by.date), PW - M, ry, { align: 'right' });
          ry += 12;
        }
        // "Thank you" sits at the bottom of the block
        doc.setFont('helvetica','normal'); doc.setFontSize(9); doc.setTextColor(100,100,100);
        doc.text('Thank you for your business!', M, ry - 4);
      } else {
        doc.setFont('helvetica','normal'); doc.setFontSize(9); doc.setTextColor(100,100,100);
        doc.text('Thank you for your business!', M, y);
      }

      const pdfBlob = doc.output('blob');
      const fileName = `Invoice-${inv.invoice_number || invoiceId}.pdf`;
      const file = new File([pdfBlob], fileName, { type: 'application/pdf' });

      setFormData(prev => ({ ...prev, invoice_file_path: '', invoice_file_name: '', invoice_file_type: '', invoice_file_size: null }));
      setInvoiceFile(file);
      setInvoiceSearchTerm('');
      setSnackbar({ open: true, message: `Invoice ${inv.invoice_number} PDF ready to attach`, severity: 'success' });
    } catch (err) {
      console.error('Error generating invoice PDF:', err);
      setSnackbar({ open: true, message: 'Error generating invoice PDF', severity: 'error' });
    } finally {
      setGeneratingInvoice(false);
    }
  };

  const fetchExports = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('exports')
        .select(`
          *,
          export_line_items (*)
        `)
        .eq('company_id', companyId)
        .order('serial_number', { ascending: false });
      if (error) throw error;
      setExports(data || []);
    } catch (err) {
      console.error('Error fetching exports:', err);
      setError('Failed to load exports.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCompanyData();
    fetchExports();
  }, [companyId]);

  const handleOpenDialog = async (exportRecord = null) => {
    if (exportRecord) {
      setEditMode(true);
      setFormData({
        id: exportRecord.id,
        serial_number: exportRecord.serial_number,
        date_of_dispatch: dayjs(exportRecord.date_of_dispatch),
        po_number: exportRecord.po_number || '',
        customer_name: exportRecord.customer_name || '',
        invoice_number: exportRecord.invoice_number || '',
        invoice_date: exportRecord.invoice_date ? dayjs(exportRecord.invoice_date) : null,
        delivery_method: exportRecord.delivery_method || '',
        driver_name: exportRecord.driver_name || '',
        delivery_location: exportRecord.delivery_location || '',
        remarks: exportRecord.remarks || '',
        invoice_file_path: exportRecord.invoice_file_path || '',
        invoice_file_name: exportRecord.invoice_file_name || '',
        invoice_file_type: exportRecord.invoice_file_type || '',
        invoice_file_size: exportRecord.invoice_file_size || null
      });
      
      // Load line items
      if (exportRecord.export_line_items && exportRecord.export_line_items.length > 0) {
        setLineItems(exportRecord.export_line_items.map(item => ({
          id: item.id,
          product_name: item.product_name || '',
          quantity: item.quantity || '',
          unit_type: item.unit_type || 'unit',
          batch_no: item.batch_no || '',
          lot_no: item.lot_no || '',
          mfg_date: item.mfg_date ? dayjs(item.mfg_date) : null,
          exp_date: item.exp_date ? dayjs(item.exp_date) : null
        })));
      } else {
        setLineItems([{
          product_name: '',
          quantity: '',
          unit_type: 'unit',
          batch_no: '',
          lot_no: '',
          mfg_date: null,
          exp_date: null
        }]);
      }
    } else {
      setEditMode(false);
      try {
        const { data, error } = await supabase
          .rpc('get_next_export_serial_number', {
            p_company_id: companyId
          });
        if (!error && data) {
          setFormData({
            id: '',
            serial_number: data,
            date_of_dispatch: dayjs(),
            po_number: '',
            customer_name: '',
            invoice_number: '',
            invoice_date: null,
            delivery_method: '',
            driver_name: '',
            delivery_location: '',
            remarks: '',
            invoice_file_path: '',
            invoice_file_name: '',
            invoice_file_type: '',
            invoice_file_size: null
          });
        }
      } catch (err) {
        console.error('Error getting serial number:', err);
      }
      
      setLineItems([{
        product_name: '',
        quantity: '',
        unit_type: 'unit',
        batch_no: '',
        lot_no: '',
        mfg_date: null,
        exp_date: null
      }]);
    }
    setInvoiceFile(null);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditMode(false);
  };

  const handleOpenPOListDialog = async () => {
    setPoListDialogOpen(true);
    setLoadingPOs(true);
    try {
      const { data, error } = await supabase
        .from('po_creation')
        .select('id, po_number, customer_name, po_date, po_status, approved, updated_at')
        .eq('company_id', companyId)
        .in('po_status', ['approved', 'sent_to_customer', 'acknowledged', 'in_production'])
        .eq('approved', true)
        .order('po_date', { ascending: false })
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Deduplicate by po_number — keep the most recent record per PI number
      const seen = new Set();
      const unique = (data || []).filter(row => {
        if (seen.has(row.po_number)) return false;
        seen.add(row.po_number);
        return true;
      });
      setAvailablePOs(unique);
    } catch (err) {
      console.error('Error fetching PIs:', err);
      setSnackbar({ open: true, message: 'Error loading PIs', severity: 'error' });
    } finally {
      setLoadingPOs(false);
    }
  };

  const handleSelectPO = async (poNumber) => {
    setLoadingAutofill(true);
    setPoListDialogOpen(false);
    
    try {
      // Fetch most recent PI record for this po_number (deduplicated)
      const { data: poRows, error: poError } = await supabase
        .from('po_creation')
        .select(`
          *,
          po_creation_items (*)
        `)
        .eq('company_id', companyId)
        .eq('po_number', poNumber)
        .order('updated_at', { ascending: false })
        .limit(1);

      if (poError || !poRows || poRows.length === 0) {
        setSnackbar({ open: true, message: 'PI not found', severity: 'error' });
        setLoadingAutofill(false);
        return;
      }
      const poData = poRows[0];

      // Fetch Invoice data using PI (Proforma Invoice) number
      const { data: invoiceData, error: invoiceError } = await supabase
        .from('invoices')
        .select(`
          *,
          invoice_items (*)
        `)
        .eq('company_id', companyId)
        .eq('purchase_order_number', poNumber)
        .maybeSingle();

      // Fill form data
      setFormData(prev => ({
        ...prev,
        po_number: poData.po_number || '',
        customer_name: poData.customer_name || '',
        invoice_number: invoiceData?.invoice_number || '',
        invoice_date: invoiceData?.invoice_date ? dayjs(invoiceData.invoice_date) : null,
        delivery_method: poData.shipping_instructions ? 'delivered' : '',
        driver_name: '',
        delivery_location: poData.shipping_street ? 
          `${poData.shipping_street || ''}, ${poData.shipping_city || ''}, ${poData.shipping_state || ''} ${poData.shipping_zip || ''}`.trim().replace(/,\s*,/g, ',').replace(/^,|,$/g, '') : '',
        remarks: poData.internal_notes || ''
      }));

      // Fill line items from PI (Proforma Invoice) or Invoice
      let items = [];
      
      if (poData.po_creation_items && poData.po_creation_items.length > 0) {
        items = poData.po_creation_items.map(item => ({
          product_name: item.product_name || item.description || '',
          quantity: item.quantity || '',
          unit_type: item.unit_type?.toLowerCase() || 'unit',
          batch_no: item.batch_number || '',
          lot_no: item.lot_number || '',
          mfg_date: item.manufactured_date ? dayjs(item.manufactured_date) : null,
          exp_date: item.expiry_date ? dayjs(item.expiry_date) : null
        }));
      } else if (invoiceData?.invoice_items && invoiceData.invoice_items.length > 0) {
        items = invoiceData.invoice_items.map(item => ({
          product_name: item.description || '',
          quantity: item.quantity || '',
          unit_type: item.unit_type?.toLowerCase() || 'unit',
          batch_no: item.batch_number || '',
          lot_no: item.lot_number || '',
          mfg_date: item.manufactured_date ? dayjs(item.manufactured_date) : null,
          exp_date: item.expiry_date ? dayjs(item.expiry_date) : null
        }));
      }

      if (items.length > 0) {
        setLineItems(items);
      }

      setSnackbar({ open: true, message: 'Data loaded successfully from PI', severity: 'success' });
      setPoSearchTerm('');
    } catch (err) {
      console.error('Error loading PI data:', err);
      setSnackbar({ open: true, message: 'Error loading PI data', severity: 'error' });
    } finally {
      setLoadingAutofill(false);
    }
  };

  const handleAddLineItem = () => {
    setLineItems([...lineItems, {
      product_name: '',
      quantity: '',
      unit_type: 'unit',
      batch_no: '',
      lot_no: '',
      mfg_date: null,
      exp_date: null
    }]);
  };

  const handleRemoveLineItem = (index) => {
    if (lineItems.length > 1) {
      const newLineItems = lineItems.filter((_, i) => i !== index);
      setLineItems(newLineItems);
    }
  };

  const handleLineItemChange = (index, field, value) => {
    const newLineItems = [...lineItems];
    newLineItems[index][field] = value;
    setLineItems(newLineItems);
  };

  const handleFileUpload = (event) => {
    const file = event.target.files?.[0];
    if (file) {
      setInvoiceFile(file);
    }
  };

  const uploadInvoiceFile = async (file) => {
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;
      const filePath = `${companyId}/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('export-documents')
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      return {
        path: filePath,
        name: file.name,
        type: file.type,
        size: file.size
      };
    } catch (error) {
      console.error('Error uploading file:', error);
      throw error;
    }
  };

  const removeInvoiceFile = async () => {
    try {
      if (formData.invoice_file_path) {
        const { error } = await supabase.storage
          .from('export-documents')
          .remove([formData.invoice_file_path]);

        if (error) throw error;
      }
      setFormData(prev => ({
        ...prev,
        invoice_file_path: '',
        invoice_file_name: '',
        invoice_file_type: '',
        invoice_file_size: null
      }));
      setInvoiceFile(null);
      setSnackbar({ open: true, message: 'Invoice file removed successfully', severity: 'success' });
    } catch (error) {
      console.error('Error removing file:', error);
      setSnackbar({ open: true, message: 'Error removing file', severity: 'error' });
    }
  };

  const downloadInvoiceFile = async (filePath, fileName) => {
    try {
      const { data, error } = await supabase.storage
        .from('export-documents')
        .download(filePath);

      if (error) throw error;

      const url = URL.createObjectURL(data);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading file:', error);
      setSnackbar({ open: true, message: 'Error downloading file', severity: 'error' });
    }
  };

  const viewInvoiceFile = async (filePath, fileName) => {
    try {
      const { data, error } = await supabase.storage
        .from('export-documents')
        .createSignedUrl(filePath, 3600);

      if (error) throw error;

      window.open(data.signedUrl, '_blank');
    } catch (error) {
      console.error('Error viewing file:', error);
      setSnackbar({ open: true, message: 'Error viewing file', severity: 'error' });
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setError(null);

    try {
      // Validate that at least one line item has data
      const hasValidLineItems = lineItems.some(item => 
        item.product_name.trim() && item.quantity
      );

      if (!hasValidLineItems) {
        setError('Please add at least one product line item');
        setSubmitting(false);
        return;
      }

      // Upload invoice file if a new one was selected
      let invoiceFileData = {
        invoice_file_path: formData.invoice_file_path,
        invoice_file_name: formData.invoice_file_name,
        invoice_file_type: formData.invoice_file_type,
        invoice_file_size: formData.invoice_file_size
      };

      if (invoiceFile) {
        setUploadingInvoice(true);
        const uploadedFile = await uploadInvoiceFile(invoiceFile);
        invoiceFileData = {
          invoice_file_path: uploadedFile.path,
          invoice_file_name: uploadedFile.name,
          invoice_file_type: uploadedFile.type,
          invoice_file_size: uploadedFile.size
        };
        setUploadingInvoice(false);
      }

      const exportData = {
        company_id: companyId,
        serial_number: parseInt(formData.serial_number),
        date_of_dispatch: formData.date_of_dispatch ? formData.date_of_dispatch.format('YYYY-MM-DD') : null,
        po_number: formData.po_number.trim() || null,
        customer_name: formData.customer_name.trim() || null,
        invoice_number: formData.invoice_number.trim() || null,
        invoice_date: formData.invoice_date ? formData.invoice_date.format('YYYY-MM-DD') : null,
        delivery_method: formData.delivery_method || null,
        driver_name: formData.delivery_method === 'delivered' ? (formData.driver_name.trim() || null) : null,
        delivery_location: formData.delivery_method === 'delivered' ? (formData.delivery_location.trim() || null) : null,
        remarks: formData.remarks.trim() || null,
        ...invoiceFileData
      };

      let exportId = formData.id;

      if (editMode) {
        const { error } = await supabase
          .from('exports')
          .update({ ...exportData, updated_at: new Date().toISOString() })
          .eq('id', formData.id);
        if (error) throw error;

        // Delete existing line items
        const { error: deleteError } = await supabase
          .from('export_line_items')
          .delete()
          .eq('export_id', formData.id);
        if (deleteError) throw deleteError;
      } else {
        const { data: newExport, error } = await supabase
          .from('exports')
          .insert(exportData)
          .select()
          .single();
        if (error) throw error;
        exportId = newExport.id;
      }

      // Insert line items
      const lineItemsData = lineItems
        .filter(item => item.product_name.trim() && item.quantity)
        .map(item => ({
          export_id: exportId,
          product_name: item.product_name.trim(),
          quantity: parseInt(item.quantity),
          unit_type: item.unit_type,
          batch_no: item.batch_no.trim() || null,
          lot_no: item.lot_no.trim() || null,
          mfg_date: item.mfg_date ? item.mfg_date.format('YYYY-MM-DD') : null,
          exp_date: item.exp_date ? item.exp_date.format('YYYY-MM-DD') : null
        }));

      if (lineItemsData.length > 0) {
        const { error: lineItemsError } = await supabase
          .from('export_line_items')
          .insert(lineItemsData);
        if (lineItemsError) throw lineItemsError;
      }

      await fetchExports();
      handleCloseDialog();
      setSnackbar({
        open: true,
        message: `Export record ${editMode ? 'updated' : 'added'} successfully`,
        severity: 'success'
      });
    } catch (err) {
      console.error('Error saving export:', err);
      setError(`Failed to ${editMode ? 'update' : 'create'} export record.`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteClick = (exportRecord) => {
    setExportToDelete(exportRecord);
    setDeleteConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!exportToDelete) return;
    
    try {
      // Delete invoice file from storage bucket if it exists
      if (exportToDelete.invoice_file_path) {
        try {
          const { error: storageError } = await supabase.storage
            .from('export-documents')
            .remove([exportToDelete.invoice_file_path]);
          
          if (storageError) {
            console.error('Error deleting invoice file from storage:', storageError);
          }
        } catch (storageErr) {
          console.error('Error deleting invoice file:', storageErr);
        }
      }

      // Delete the export record (line items will be cascade deleted)
      const { error } = await supabase
        .from('exports')
        .delete()
        .eq('id', exportToDelete.id);
      if (error) throw error;
      
      await fetchExports();
      setSnackbar({ open: true, message: 'Export record deleted successfully', severity: 'success' });
    } catch (err) {
      setSnackbar({ open: true, message: `Error deleting record: ${err.message}`, severity: 'error' });
    }
    setDeleteConfirmOpen(false);
    setExportToDelete(null);
  };

  const handleReviewExport = (exportRecord) => {
    setApprovalExport(exportRecord);
    setSignatureName('');
    setApprovalNotes('');
    setApprovalDialogOpen(true);
  };

  const handleApprovalSubmit = async () => {
    try {
      if (!signatureName.trim()) {
        setSnackbar({ open: true, message: 'Signature name is required for approval', severity: 'error' });
        return;
      }

      const approvalData = {
        approved_by_signature: signatureName,
        approved_at: new Date().toISOString(),
        approval_notes: approvalNotes || null,
        status: 'approved'
      };

      const { error } = await supabase
        .from('exports')
        .update(approvalData)
        .eq('id', approvalExport.id);

      if (error) throw error;

      setApprovalDialogOpen(false);
      setSignatureName('');
      setApprovalNotes('');
      setApprovalExport(null);
      await fetchExports();
      setSnackbar({ open: true, message: 'Export record approved successfully', severity: 'success' });
    } catch (err) {
      console.error('Error approving export:', err);
      setSnackbar({ open: true, message: `Error approving record: ${err.message}`, severity: 'error' });
    }
  };

  const handleUnapproveExport = async (exportRecord) => {
    try {
      const unapprovalData = {
        approved_by_signature: null,
        approved_at: null,
        approval_notes: null,
        status: 'pending'
      };

      const { error } = await supabase
        .from('exports')
        .update(unapprovalData)
        .eq('id', exportRecord.id);

      if (error) throw error;

      await fetchExports();
      setSnackbar({ open: true, message: 'Export record unapproved successfully', severity: 'success' });
    } catch (err) {
      console.error('Error unapproving export:', err);
      setSnackbar({ open: true, message: `Error unapproving record: ${err.message}`, severity: 'error' });
    }
  };

  const handleViewExport = (exportRecord) => {
    setViewExport(exportRecord);
    setViewDialogOpen(true);
  };

  const formatDate = (date) => {
    if (!date) return '—';
    return dayjs(date).format('MMM DD, YYYY');
  };

  const formatNumber = (value) => {
    if (!value && value !== 0) return '—';
    return new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 3 }).format(value);
  };

  // Filter exports based on search term
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredExports(exports);
      return;
    }
    const searchLower = searchTerm.toLowerCase();
    const filtered = exports.filter(exp => 
      exp.customer_name?.toLowerCase().includes(searchLower) ||
      exp.po_number?.toLowerCase().includes(searchLower) ||
      exp.invoice_number?.toLowerCase().includes(searchLower) ||
      exp.export_line_items?.some(item => 
        item.product_name?.toLowerCase().includes(searchLower) ||
        item.batch_no?.toLowerCase().includes(searchLower) ||
        item.lot_no?.toLowerCase().includes(searchLower)
      )
    );
    setFilteredExports(filtered);
    setPage(0);
  }, [searchTerm, exports]);

  // Permission check
  if (!canViewExports()) {
    return (
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          justifyContent: 'center',
          minHeight: '70vh',
          textAlign: 'center'
        }}>
          <Card sx={{
            p: 8,
            borderRadius: 3,
            border: `1px solid ${theme.palette.grey[400]}`,
            background: 'white',
            boxShadow: `0 12px 40px ${alpha(theme.palette.grey[900], 0.08)}`,
            maxWidth: 600,
            width: '100%',
            position: 'relative',
            overflow: 'hidden',
            cursor: 'default',
            transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
            '&:hover': {
              transform: 'translateY(-8px)',
              boxShadow: `0 20px 60px ${alpha(theme.palette.grey[900], 0.15)}`,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
            },
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: 4,
              background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.grey[400]})`,
              opacity: 0.8,
              transition: 'opacity 0.3s ease'
            },
            '&:hover::before': {
              opacity: 1
            }
          }}>
            <Box sx={{ 
              p: 3, 
              borderRadius: 3, 
              background: `linear-gradient(${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.primary.main, 0.02)})`,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
              mb: 6,
              display: 'inline-flex',
              boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.15)}`
            }}>
              <OutputIcon sx={{ fontSize: 48, color: 'text.secondary', transform: 'scaleX(-1)' }} />
            </Box>
            <Typography variant="h3" sx={{ 
              mb: 3, 
              fontWeight: 500, 
              color: 'text.primary',
              fontSize: '2.5rem',
              letterSpacing: '-0.8px'
            }}>
              Access Restricted
            </Typography>
            <Typography variant="h6" sx={{ 
              color: 'text.secondary', 
              mb: 4,
              fontWeight: 500,
              fontSize: '1.2rem'
            }}>
              Exports Permissions Required
            </Typography>
            <Typography variant="body1" sx={{ 
              color: 'text.secondary',
              lineHeight: 1.7,
              mb: 6,
              fontSize: '1.05rem',
              maxWidth: 450,
              mx: 'auto'
            }}>
              You don't currently have the necessary permissions to access the Exports section. 
              Contact your system administrator to request access or verify your role permissions.
            </Typography>
            <Button
              variant="outlined"
              onClick={() => navigate(-1)}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 500,
                px: 4,
                py: 1.5,
                fontSize: '1rem'
              }}
            >
              Return to Previous Page
            </Button>
          </Card>
        </Box>
      </Container>
    );
  }

  if (loading) {
    return (
      <Container disableGutters maxWidth="xl" sx={{ px: 3, py: 4, pb: 16 }}>
        <Card sx={{ 
          borderRadius: 3,
          mt: 10,
          mx: 'auto',
          maxWidth: 600,
          border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
          background: `linear-gradient(135deg, ${alpha(theme.palette.grey[100], 0.8)}, ${alpha(theme.palette.grey[50], 0.9)})`,
          boxShadow: `0 12px 32px ${alpha('#009688', 0.15)}`,
        }}>
          <CardContent sx={{ p: 6, textAlign: 'center' }}>
            <Box sx={{ mb: 3 }}>
              <Box sx={{
                display: 'inline-flex',
                p: 3,
                borderRadius: '50%',
                bgcolor: alpha('#009688', 0.1),
                mb: 2
              }}>
                <OutputIcon sx={{ fontSize: 48, color: '#009688', transform: 'scaleX(-1)' }} />
              </Box>
            </Box>
            <Typography variant="h6" sx={{
              fontWeight: 500,
              color: '#009688',
              mb: 1,
              fontSize: '1.1rem'
            }}>
              Loading Exports
            </Typography>
            <Typography variant="body2" sx={{
              color: 'text.secondary',
              fontWeight: 500,
              maxWidth: 400,
              mx: 'auto',
              lineHeight: 1.6,
              mb: 3
            }}>
              Analyzing export data...
            </Typography>
            <Box sx={{
              width: '60%',
              mx: 'auto',
              '& .MuiLinearProgress-root': {
                height: 8,
                borderRadius: 4,
                bgcolor: alpha(theme.palette.grey[300], 0.3),
                '& .MuiLinearProgress-bar': {
                  bgcolor: '#009688',
                  borderRadius: 4
                }
              }
            }}>
              <LinearProgress />
            </Box>
          </CardContent>
        </Card>
      </Container>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Fade in timeout={800}>
        <Box>
          <Container maxWidth="xl" sx={{ py: 3, pb: 16 }}>
            {/* Header Section */}
            <Box sx={{ mb: 3 }}>
              <Card sx={{ 
                borderRadius: 3,
                mt: 5,
                mb: 3,
                border: `1px solid ${theme.palette.grey[400]}`,
                background: theme.palette.grey[100],
                backdropFilter: 'blur(20px)',
                borderTop: '3px solid #009688',
                boxShadow: 'none',
                position: 'relative',
                overflow: 'hidden',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
                },
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              }}>
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5, mb: 2 }}>
                    <Box sx={{
                      p: 2,
                      borderRadius: 2,
                      bgcolor: alpha('#009688', 0.08),
                    }}>
                      <OutputIcon sx={{ fontSize: 32, color: '#009688', transform: 'scaleX(-1)' }} />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="h4" sx={{
                        fontWeight: 500,
                        color: '#009688',
                        mb: 1,
                        fontSize: '2rem',
                        letterSpacing: '-0.5px'
                      }}>
                        Exports Log
                      </Typography>
                      <Typography variant="subtitle1" sx={{ 
                        color: 'text.secondary', 
                        fontWeight: 500,
                        fontSize: '1rem'
                      }}>
                        {companyName}
                      </Typography>
                    </Box>
                  </Box>
                  <Typography variant="body2" sx={{ 
                    color: 'text.secondary', 
                    maxWidth: 700, 
                    lineHeight: 1.6,
                    fontSize: '0.95rem',
                    fontWeight: 500
                  }}>
                    Track and manage export records, including dispatch details, customer information, and product line items.
                  </Typography>
                </CardContent>
              </Card>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>{error}</Alert>}

            {/* Search Bar */}
            <Card sx={{
              height: 100,
              mb: 2,
              border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
              borderRadius: 3,
              background: `linear-gradient(135deg, ${alpha(theme.palette.grey[100], 0.8)}, ${alpha(theme.palette.grey[50], 0.9)})`,
              boxShadow: `0 8px 24px ${alpha(theme.palette.grey[500], 0.12)}`,
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: `0 20px 40px ${alpha(theme.palette.primary.main, 0.25)}`,
                border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
              }
            }}>
              <CardContent>
                <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
                  <TextField
                    placeholder="Search exports..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    size="small"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      minWidth: 280,
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 3,
                        border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                        background: 'white',
                        height: 40,
                        '& fieldset': { border: 'none' },
                        '&:hover': {
                          border: `1px solid ${theme.palette.primary.main}`,
                          boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`
                        },
                        '&.Mui-focused': {
                          border: `1px solid ${theme.palette.primary.main}`,
                          boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.25)}`
                        }
                      },
                      '& .MuiInputBase-input': {
                        fontSize: '0.9rem',
                        fontWeight: 500,
                        py: 1.25
                      }
                    }}
                  />
                  
                  <Box sx={{ flex: 1 }} />
                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                    {`${filteredExports.length} export record${filteredExports.length !== 1 ? 's' : ''}`}
                  </Typography>
                </Box>
              </CardContent>
            </Card>

            {/* Exports Table */}
            <Paper sx={{ 
              borderRadius: 3, 
              border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
              background: `linear-gradient(135deg, ${alpha(theme.palette.grey[100], 0.8)}, ${alpha(theme.palette.grey[50], 0.9)})`,
              boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.15)}`,
              overflow: 'hidden',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: `0 20px 40px ${alpha(theme.palette.primary.main, 0.25)}`,
                border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
              }
            }}>
              <TableContainer sx={{ 
                overflow: 'auto',
                '&::-webkit-scrollbar': {
                  display: 'none'
                },
                msOverflowStyle: 'none',
                scrollbarWidth: 'none'
              }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 500, fontSize: '0.85rem', bgcolor: 'white', borderBottom: `2px solid ${theme.palette.primary.main}`, color: 'primary.main', py: 2, px: 2 }}>S.No</TableCell>
                      <TableCell sx={{ fontWeight: 500, fontSize: '0.85rem', bgcolor: 'white', borderBottom: `2px solid ${theme.palette.primary.main}`, color: 'primary.main', py: 2, px: 2 }}>Date of Dispatch</TableCell>
                      <TableCell sx={{ fontWeight: 500, fontSize: '0.85rem', bgcolor: 'white', borderBottom: `2px solid ${theme.palette.primary.main}`, color: 'primary.main', py: 2, px: 2 }}>P.I Number</TableCell>
                      <TableCell sx={{ fontWeight: 500, fontSize: '0.85rem', bgcolor: 'white', borderBottom: `2px solid ${theme.palette.primary.main}`, color: 'primary.main', py: 2, px: 2 }}>Customer Name</TableCell>
                      <TableCell sx={{ fontWeight: 500, fontSize: '0.85rem', bgcolor: 'white', borderBottom: `2px solid ${theme.palette.primary.main}`, color: 'primary.main', py: 2, px: 2 }}>Invoice Number</TableCell>
                      <TableCell sx={{ fontWeight: 500, fontSize: '0.85rem', bgcolor: 'white', borderBottom: `2px solid ${theme.palette.primary.main}`, color: 'primary.main', py: 2, px: 2 }}>Products</TableCell>
                      <TableCell sx={{ fontWeight: 500, fontSize: '0.85rem', bgcolor: 'white', borderBottom: `2px solid ${theme.palette.primary.main}`, color: 'primary.main', py: 2, px: 2 }}>Status</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 500, fontSize: '0.85rem', bgcolor: 'white', borderBottom: `2px solid ${theme.palette.primary.main}`, color: 'primary.main', py: 2, px: 2, width: 180 }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredExports.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((exp, index) => (
                      <TableRow 
                        key={`export-row-${exp.id}-${index}`}
                        hover
                        onClick={() => handleViewExport(exp)}
                        sx={{ 
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          bgcolor: 'white',
                          '&:hover': { 
                            bgcolor: 'grey.50',
                            transform: 'translateX(4px)',
                            transition: 'all 0.2s ease',
                            boxShadow: 'inset 4px 0 0 black'
                          },
                          '& .MuiTableCell-root': {
                            borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.6)}`,
                            py: 2,
                            px: 2,
                            fontSize: '0.85rem'
                          }
                        }}
                      >
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {exp.serial_number}
                          </Typography>
                        </TableCell>
                        <TableCell>{formatDate(exp.date_of_dispatch)}</TableCell>
                        <TableCell>{exp.po_number || '—'}</TableCell>
                        <TableCell>{exp.customer_name || '—'}</TableCell>
                        <TableCell>{exp.invoice_number || '—'}</TableCell>
                        <TableCell>
                          {exp.export_line_items && exp.export_line_items.length > 0 ? (
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {exp.export_line_items[0].product_name}
                              </Typography>
                              {exp.export_line_items.length > 1 && (
                                <Typography variant="caption" color="text.secondary">
                                  +{exp.export_line_items.length - 1} more
                                </Typography>
                              )}
                            </Box>
                          ) : (
                            '—'
                          )}
                        </TableCell>
                        <TableCell>
                          {exp.status === 'approved' ? (
                            <Chip label="Approved" color="success" size="small" icon={<VerifiedIcon />} />
                          ) : (
                            <Chip label="Pending" color="default" size="small" />
                          )}
                        </TableCell>
                        <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                          <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                            <Tooltip title={canEditExports() ? "Edit" : "No edit permission"}>
                              <span>
                                <IconButton 
                                  size="small"
                                  onClick={() => handleOpenDialog(exp)}
                                  disabled={!canEditExports()}
                                  sx={{
                                    color: canEditExports() ? 'primary.main' : 'action.disabled',
                                    border: `1px solid ${alpha(theme.palette.grey[400], 0.3)}`,
                                    borderRadius: 1,
                                    transition: 'all 0.2s cubic-bezier(0.4, 0.0, 0.2, 1)',
                                    '&:hover': canEditExports() ? {
                                      bgcolor: 'primary.main',
                                      color: 'white',
                                      transform: 'scale(1.05)',
                                      boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.3)}`
                                    } : {},
                                    '&.Mui-disabled': {
                                      color: 'action.disabled',
                                      border: `1px solid ${alpha(theme.palette.grey[400], 0.2)}`
                                    }
                                  }}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                            <Tooltip title={canDeleteExports() ? "Delete" : "No delete permission"}>
                              <span>
                                <IconButton 
                                  size="small"
                                  onClick={() => handleDeleteClick(exp)}
                                  disabled={!canDeleteExports()}
                                  sx={{
                                    color: canDeleteExports() ? 'error.main' : 'action.disabled',
                                    border: `1px solid ${alpha(theme.palette.grey[400], 0.3)}`,
                                    borderRadius: 1,
                                    transition: 'all 0.2s cubic-bezier(0.4, 0.0, 0.2, 1)',
                                    '&:hover': canDeleteExports() ? {
                                      bgcolor: 'error.main',
                                      color: 'white',
                                      transform: 'scale(1.05)',
                                      boxShadow: `0 2px 8px ${alpha(theme.palette.error.main, 0.3)}`
                                    } : {},
                                    '&.Mui-disabled': {
                                      color: 'action.disabled',
                                      border: `1px solid ${alpha(theme.palette.grey[400], 0.2)}`
                                    }
                                  }}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                            {exp.status !== 'approved' ? (
                              <Tooltip title={canApproveExports() ? "Approve" : "No approve permission"}>
                                <span>
                                  <IconButton 
                                    size="small"
                                    onClick={() => handleReviewExport(exp)}
                                    disabled={!canApproveExports()}
                                    sx={{
                                      color: canApproveExports() ? 'success.main' : 'action.disabled',
                                      border: `1px solid ${alpha(theme.palette.grey[400], 0.3)}`,
                                      borderRadius: 1,
                                      transition: 'all 0.2s cubic-bezier(0.4, 0.0, 0.2, 1)',
                                      '&:hover': canApproveExports() ? {
                                        bgcolor: 'success.main',
                                        color: 'white',
                                        transform: 'scale(1.05)',
                                        boxShadow: `0 2px 8px ${alpha(theme.palette.success.main, 0.3)}`
                                      } : {},
                                      '&.Mui-disabled': {
                                        color: 'action.disabled',
                                        border: `1px solid ${alpha(theme.palette.grey[400], 0.2)}`
                                      }
                                    }}
                                  >
                                    <CheckIcon fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            ) : (
                              <Tooltip title={canApproveExports() ? "Unapprove" : "No approve permission"}>
                                <span>
                                  <IconButton 
                                    size="small"
                                    onClick={() => handleUnapproveExport(exp)}
                                    disabled={!canApproveExports()}
                                    sx={{
                                      color: canApproveExports() ? 'warning.main' : 'action.disabled',
                                      border: `1px solid ${alpha(theme.palette.grey[400], 0.3)}`,
                                      borderRadius: 1,
                                      transition: 'all 0.2s cubic-bezier(0.4, 0.0, 0.2, 1)',
                                      '&:hover': canApproveExports() ? {
                                        bgcolor: 'warning.main',
                                        color: 'white',
                                        transform: 'scale(1.05)',
                                        boxShadow: `0 2px 8px ${alpha(theme.palette.warning.main, 0.3)}`
                                      } : {},
                                      '&.Mui-disabled': {
                                        color: 'action.disabled',
                                        border: `1px solid ${alpha(theme.palette.grey[400], 0.2)}`
                                      }
                                    }}
                                  >
                                    <CancelIcon fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                    {filteredExports.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                          <Typography variant="body2" color="text.secondary">
                            No export records yet. Click the + button to add the first record.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'center', 
                mt: 3,
                mb: 4,
                px: 3
              }}>
                <Paper sx={{
                  borderRadius: 3,
                  border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                  background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.primary.main, 0.02)})`,
                  boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.15)}`,
                  overflow: 'hidden'
                }}>
                  <TablePagination
                    component="div"
                    count={filteredExports.length}
                    page={page}
                    onPageChange={(e, newPage) => setPage(newPage)}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={(e) => {
                      setRowsPerPage(parseInt(e.target.value, 10));
                      setPage(0);
                    }}
                    rowsPerPageOptions={[10, 25, 50]}
                  />
                </Paper>
              </Box>
            </Paper>

            {canEditExports() && (
              <Tooltip title="Add Export Record">
                <Fab
                  color="primary"
                  onClick={() => handleOpenDialog()}
                  sx={{ 
                    position: 'fixed', 
                    bottom: 24, 
                    right: 24, 
                    width: 100, 
                    height: 100
                  }}
                >
                  <AddIcon sx={{ fontSize: 32 }} />
                </Fab>
              </Tooltip>
            )}

            {/* Add/Edit Dialog */}
            <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="lg" fullWidth>
              <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>{editMode ? 'Edit Export Record' : 'Add Export Record'}</Box>
                {!editMode && (
                  <Button
                    variant="outlined"
                    startIcon={<OutputIcon sx={{ transform: 'scaleX(-1)' }} />}
                    onClick={handleOpenPOListDialog}
                    size="small"
                  >
                    Autofill from PI
                  </Button>
                )}
              </DialogTitle>
              <DialogContent>
                <Box sx={{ pt: 2 }}>
                  <Grid container spacing={3}>

                    {/* General Information Section */}
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" sx={{ 
                        fontWeight: 500, 
                        color: 'primary.main',
                        mb: 1,
                        fontSize: '1.1rem'
                      }}>
                        General Information
                      </Typography>
                      <Divider sx={{ mb: 2 }} />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        label="S.No"
                        value={formData.serial_number}
                        disabled
                        fullWidth
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <DatePicker
                        label="Date of Dispatch"
                        value={formData.date_of_dispatch}
                        onChange={(newValue) => setFormData(prev => ({ ...prev, date_of_dispatch: newValue }))}
                        slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        label="P.I Number"
                        value={formData.po_number}
                        onChange={(e) => setFormData(prev => ({ ...prev, po_number: e.target.value }))}
                        fullWidth
                        size="small"
                      />
                    </Grid>

                    {/* Customer Details Section */}
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" sx={{ 
                        fontWeight: 500, 
                        color: 'primary.main',
                        mb: 1,
                        mt: 2,
                        fontSize: '1.1rem'
                      }}>
                        Customer Details
                      </Typography>
                      <Divider sx={{ mb: 2 }} />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        label="Customer Name"
                        value={formData.customer_name}
                        onChange={(e) => setFormData(prev => ({ ...prev, customer_name: e.target.value }))}
                        fullWidth
                        size="small"
                        required
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        label="Invoice Number"
                        value={formData.invoice_number}
                        onChange={(e) => setFormData(prev => ({ ...prev, invoice_number: e.target.value }))}
                        fullWidth
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <DatePicker
                        label="Invoice Date"
                        value={formData.invoice_date}
                        onChange={(newValue) => setFormData(prev => ({ ...prev, invoice_date: newValue }))}
                        slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                      />
                    </Grid>

                    {/* Delivery Section */}
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" sx={{ 
                        fontWeight: 500, 
                        color: 'primary.main',
                        mb: 1,
                        mt: 2,
                        fontSize: '1.1rem'
                      }}>
                        Delivery Information
                      </Typography>
                      <Divider sx={{ mb: 2 }} />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Delivery Method</InputLabel>
                        <Select
                          value={formData.delivery_method}
                          label="Delivery Method"
                          onChange={(e) => setFormData(prev => ({ 
                            ...prev, 
                            delivery_method: e.target.value,
                            // Clear delivery fields if switching to picked up
                            driver_name: e.target.value === 'picked_up' ? '' : prev.driver_name,
                            delivery_location: e.target.value === 'picked_up' ? '' : prev.delivery_location
                          }))}
                        >
                          <MenuItem value="">Select Method</MenuItem>
                          <MenuItem value="picked_up">Picked Up</MenuItem>
                          <MenuItem value="delivered">Delivered</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    
                    {formData.delivery_method === 'delivered' && (
                      <>
                        <Grid item xs={12} md={4}>
                          <TextField
                            label="Driver Name"
                            value={formData.driver_name}
                            onChange={(e) => setFormData(prev => ({ ...prev, driver_name: e.target.value }))}
                            fullWidth
                            size="small"
                          />
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <TextField
                            label="Delivery Location"
                            value={formData.delivery_location}
                            onChange={(e) => setFormData(prev => ({ ...prev, delivery_location: e.target.value }))}
                            fullWidth
                            size="small"
                          />
                        </Grid>
                      </>
                    )}
                    
                    {formData.delivery_method === 'picked_up' && (
                      <Grid item xs={12} md={8}>
                        <Box sx={{ 
                          p: 2, 
                          bgcolor: alpha(theme.palette.info.main, 0.1),
                          borderRadius: 2,
                          border: `1px solid ${alpha(theme.palette.info.main, 0.3)}`
                        }}>
                          <Typography variant="body2" color="info.main" sx={{ fontWeight: 500 }}>
                            Order marked as Picked Up - No delivery details required
                          </Typography>
                        </Box>
                      </Grid>
                    )}

                    {/* Product Line Items Section */}
                    <Grid item xs={12}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, mt: 2 }}>
                        <Typography variant="subtitle2" sx={{ 
                          fontWeight: 500, 
                          color: 'primary.main',
                          fontSize: '1.1rem'
                        }}>
                          Product Details
                        </Typography>
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<AddIcon />}
                          onClick={handleAddLineItem}
                        >
                          Add Product
                        </Button>
                      </Box>
                      <Divider sx={{ mb: 2 }} />
                    </Grid>

                    {lineItems.map((item, index) => (
                      <Grid item xs={12} key={index}>
                        <Card sx={{ p: 2, bgcolor: alpha(theme.palette.grey[100], 0.5), position: 'relative', pt: lineItems.length > 1 ? 5 : 2 }}>
                          {lineItems.length > 1 && (
                            <Box sx={{ 
                              position: 'absolute', 
                              top: 8, 
                              right: 8,
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                              zIndex: 1
                            }}>
                              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                                Product {index + 1}
                              </Typography>
                              <Tooltip title={canDeleteExports() ? "Remove product" : "No delete permission"}>
                                <span>
                                  <IconButton
                                    size="small"
                                    onClick={() => handleRemoveLineItem(index)}
                                    disabled={!canDeleteExports()}
                                    sx={{ 
                                      color: canDeleteExports() ? 'error.main' : 'action.disabled',
                                      bgcolor: 'white',
                                      border: `1px solid ${alpha(theme.palette.error.main, 0.3)}`,
                                      '&:hover': canDeleteExports() ? {
                                        bgcolor: 'error.main',
                                        color: 'white',
                                        transform: 'scale(1.1)'
                                      } : {},
                                      '&.Mui-disabled': {
                                        color: 'action.disabled',
                                        border: `1px solid ${alpha(theme.palette.grey[400], 0.2)}`
                                      }
                                    }}
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            </Box>
                          )}
                          <Grid container spacing={2}>
                            <Grid item xs={12} md={6}>
                              <TextField
                                label="Product Name"
                                value={item.product_name}
                                onChange={(e) => handleLineItemChange(index, 'product_name', e.target.value)}
                                fullWidth
                                size="small"
                                required
                              />
                            </Grid>
                            <Grid item xs={12} md={3}>
                              <TextField
                                label="Quantity"
                                value={item.quantity}
                                onChange={(e) => handleLineItemChange(index, 'quantity', e.target.value)}
                                fullWidth
                                size="small"
                                type="number"
                                required
                                inputProps={{ 
                                  step: "1", 
                                  min: "1"
                                }}
                              />
                            </Grid>
                            <Grid item xs={12} md={3}>
                              <FormControl fullWidth size="small">
                                <InputLabel>Unit Type</InputLabel>
                                <Select
                                  value={item.unit_type}
                                  label="Unit Type"
                                  onChange={(e) => handleLineItemChange(index, 'unit_type', e.target.value)}
                                  renderValue={(value) => getUnitLabel(value, item.quantity)}
                                >
                                  {UNIT_TYPES.map(unit => (
                                    <MenuItem key={unit.id} value={unit.id}>
                                      {item.quantity && parseInt(item.quantity) > 1 ? unit.plural : unit.name}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            </Grid>
                            <Grid item xs={12} md={3}>
                              <TextField
                                label="Batch No."
                                value={item.batch_no}
                                onChange={(e) => handleLineItemChange(index, 'batch_no', e.target.value)}
                                fullWidth
                                size="small"
                              />
                            </Grid>
                            <Grid item xs={12} md={3}>
                              <TextField
                                label="Lot No."
                                value={item.lot_no}
                                onChange={(e) => handleLineItemChange(index, 'lot_no', e.target.value)}
                                fullWidth
                                size="small"
                              />
                            </Grid>
                            <Grid item xs={12} md={3}>
                              <DatePicker
                                label="Mfg Date"
                                value={item.mfg_date}
                                onChange={(newValue) => handleLineItemChange(index, 'mfg_date', newValue)}
                                slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                              />
                            </Grid>
                            <Grid item xs={12} md={3}>
                              <DatePicker
                                label="Exp Date"
                                value={item.exp_date}
                                onChange={(newValue) => handleLineItemChange(index, 'exp_date', newValue)}
                                slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                              />
                            </Grid>
                          </Grid>
                        </Card>
                      </Grid>
                    ))}

                    {/* Additional Notes Section */}
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" sx={{ 
                        fontWeight: 500, 
                        color: 'primary.main',
                        mb: 1,
                        mt: 2,
                        fontSize: '1.1rem'
                      }}>
                        Additional Notes
                      </Typography>
                      <Divider sx={{ mb: 2 }} />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        label="Remarks"
                        value={formData.remarks}
                        onChange={(e) => setFormData(prev => ({ ...prev, remarks: e.target.value }))}
                        fullWidth
                        multiline
                        rows={3}
                        size="small"
                      />
                    </Grid>

                    {/* Invoice Upload Section */}
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" sx={{ 
                        fontWeight: 500, 
                        color: 'primary.main',
                        mb: 1,
                        mt: 2,
                        fontSize: '1.1rem'
                      }}>
                        Invoice/Document
                      </Typography>
                      <Divider sx={{ mb: 2 }} />
                    </Grid>
                    <Grid item xs={12}>
                      {formData.invoice_file_name ? (
                        <Card sx={{ p: 2, bgcolor: alpha(theme.palette.primary.main, 0.05) }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <DescriptionIcon sx={{ color: 'primary.main' }} />
                            <Box sx={{ flex: 1 }}>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {formData.invoice_file_name}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {formData.invoice_file_size ? `${(formData.invoice_file_size / 1024).toFixed(2)} KB` : ''}
                              </Typography>
                            </Box>
                            <Button
                              size="small"
                              startIcon={<VisibilityIcon />}
                              onClick={() => viewInvoiceFile(formData.invoice_file_path, formData.invoice_file_name)}
                            >
                              View
                            </Button>
                            <Tooltip title={canDeleteExports() ? "Remove file" : "No delete permission"}>
                              <span>
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={removeInvoiceFile}
                                  disabled={!canDeleteExports()}
                                  sx={{
                                    '&.Mui-disabled': {
                                      color: 'action.disabled'
                                    }
                                  }}
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </Box>
                        </Card>
                      ) : (
                        <Box sx={{ display: 'flex', gap: 2 }}>
                          <Button
                            component="label"
                            variant="outlined"
                            startIcon={<CloudUploadIcon />}
                            sx={{ flex: 1, py: 2 }}
                          >
                            Upload Invoice/Document
                            <input
                              type="file"
                              hidden
                              accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                              onChange={handleFileUpload}
                            />
                          </Button>
                          <Button
                            variant="outlined"
                            startIcon={<DescriptionIcon />}
                            onClick={handleOpenInvoiceSelectDialog}
                            disabled={generatingInvoice}
                            sx={{ flex: 1, py: 2 }}
                          >
                            {generatingInvoice ? 'Generating...' : 'Select from Invoices'}
                          </Button>
                        </Box>
                      )}
                      {invoiceFile && !formData.invoice_file_name && (
                        <Box sx={{ mt: 2, p: 2, bgcolor: alpha(theme.palette.success.main, 0.1), borderRadius: 1 }}>
                          <Typography variant="body2" color="success.main" sx={{ fontWeight: 500 }}>
                            <AttachFileIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 1 }} />
                            {invoiceFile.name} ({(invoiceFile.size / 1024).toFixed(2)} KB) - Ready to upload
                          </Typography>
                        </Box>
                      )}
                    </Grid>
                  </Grid>
                </Box>
              </DialogContent>
              <DialogActions>
                <Button onClick={handleCloseDialog}>Cancel</Button>
                <Button onClick={handleSubmit} variant="contained" disabled={submitting || uploadingInvoice}>
                  {uploadingInvoice ? 'Uploading...' : submitting ? 'Saving...' : editMode ? 'Update' : 'Add'}
                </Button>
              </DialogActions>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
              <DialogTitle>Confirm Delete</DialogTitle>
              <DialogContent>
                <Typography>Are you sure you want to delete this export record?</Typography>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
                <Button onClick={handleConfirmDelete} variant="contained" color="error">
                  Delete
                </Button>
              </DialogActions>
            </Dialog>

            {/* View Details Dialog */}
            <Dialog
              open={viewDialogOpen}
              onClose={() => setViewDialogOpen(false)}
              maxWidth="md"
              fullWidth
            >
              <DialogTitle sx={{ 
                fontWeight: 500,
                color: 'primary.main',
                borderBottom: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.primary.main, 0.03)})`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 2
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <VisibilityIcon sx={{ color: 'primary.main' }} />
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 500 }}>
                      Export Record Details
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Serial No: {viewExport?.serial_number}
                    </Typography>
                  </Box>
                </Box>
                <IconButton onClick={() => setViewDialogOpen(false)} size="small">
                  <CloseIcon />
                </IconButton>
              </DialogTitle>
              
              <DialogContent sx={{ mt: 3 }}>
                {viewExport && (
                  <Grid container spacing={3}>
                    {/* General Information */}
                    <Grid item xs={12}>
                      <Typography variant="subtitle1" sx={{ 
                        fontWeight: 500, 
                        color: 'primary.main',
                        mb: 2,
                        pb: 1,
                        borderBottom: `2px solid ${alpha(theme.palette.primary.main, 0.2)}`
                      }}>
                        General Information
                      </Typography>
                    </Grid>
                    
                    <Grid item xs={12} md={6}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, display: 'block', mb: 0.5 }}>
                        Date of Dispatch
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {formatDate(viewExport.date_of_dispatch)}
                      </Typography>
                    </Grid>
                    
                    <Grid item xs={12} md={6}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, display: 'block', mb: 0.5 }}>
                        P.I Number
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {viewExport.po_number || '—'}
                      </Typography>
                    </Grid>

                    {/* Customer Details */}
                    <Grid item xs={12}>
                      <Typography variant="subtitle1" sx={{ 
                        fontWeight: 500, 
                        color: 'primary.main',
                        mb: 2,
                        pb: 1,
                        mt: 2,
                        borderBottom: `2px solid ${alpha(theme.palette.primary.main, 0.2)}`
                      }}>
                        Customer Details
                      </Typography>
                    </Grid>
                    
                    <Grid item xs={12} md={4}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, display: 'block', mb: 0.5 }}>
                        Customer Name
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {viewExport.customer_name || '—'}
                      </Typography>
                    </Grid>
                    
                    <Grid item xs={12} md={4}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, display: 'block', mb: 0.5 }}>
                        Invoice Number
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {viewExport.invoice_number || '—'}
                      </Typography>
                    </Grid>
                    
                    <Grid item xs={12} md={4}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, display: 'block', mb: 0.5 }}>
                        Invoice Date
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {formatDate(viewExport.invoice_date)}
                      </Typography>
                    </Grid>

                    {/* Delivery Information */}
                    {viewExport.delivery_method && (
                      <>
                        <Grid item xs={12}>
                          <Typography variant="subtitle1" sx={{ 
                            fontWeight: 500, 
                            color: 'primary.main',
                            mb: 2,
                            pb: 1,
                            mt: 2,
                            borderBottom: `2px solid ${alpha(theme.palette.primary.main, 0.2)}`
                          }}>
                            Delivery Information
                          </Typography>
                        </Grid>
                        
                        <Grid item xs={12} md={4}>
                          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, display: 'block', mb: 0.5 }}>
                            Delivery Method
                          </Typography>
                          <Chip 
                            label={viewExport.delivery_method === 'picked_up' ? 'Picked Up' : 'Delivered'} 
                            color={viewExport.delivery_method === 'delivered' ? 'primary' : 'default'} 
                            size="small"
                          />
                        </Grid>
                        
                        {viewExport.delivery_method === 'delivered' && (
                          <>
                            <Grid item xs={12} md={4}>
                              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, display: 'block', mb: 0.5 }}>
                                Driver Name
                              </Typography>
                              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                {viewExport.driver_name || '—'}
                              </Typography>
                            </Grid>
                            
                            <Grid item xs={12} md={4}>
                              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, display: 'block', mb: 0.5 }}>
                                Delivery Location
                              </Typography>
                              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                {viewExport.delivery_location || '—'}
                              </Typography>
                            </Grid>
                          </>
                        )}
                      </>
                    )}

                    {/* Product Details */}
                    <Grid item xs={12}>
                      <Typography variant="subtitle1" sx={{ 
                        fontWeight: 500, 
                        color: 'primary.main',
                        mb: 2,
                        pb: 1,
                        mt: 2,
                        borderBottom: `2px solid ${alpha(theme.palette.primary.main, 0.2)}`
                      }}>
                        Product Details
                      </Typography>
                    </Grid>
                    
                    <Grid item xs={12}>
                      {viewExport.export_line_items && viewExport.export_line_items.length > 0 ? (
                        <TableContainer component={Paper} sx={{ bgcolor: alpha(theme.palette.grey[100], 0.3) }}>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell sx={{ fontWeight: 500 }}>Product Name</TableCell>
                                <TableCell sx={{ fontWeight: 500 }}>Quantity</TableCell>
                                <TableCell sx={{ fontWeight: 500 }}>Batch No.</TableCell>
                                <TableCell sx={{ fontWeight: 500 }}>Lot No.</TableCell>
                                <TableCell sx={{ fontWeight: 500 }}>Mfg Date</TableCell>
                                <TableCell sx={{ fontWeight: 500 }}>Exp Date</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {viewExport.export_line_items.map((item, idx) => (
                                <TableRow key={idx}>
                                  <TableCell>{item.product_name}</TableCell>
                                  <TableCell>
                                    {item.quantity} {getUnitLabel(item.unit_type, item.quantity)}
                                  </TableCell>
                                  <TableCell>{item.batch_no || '—'}</TableCell>
                                  <TableCell>{item.lot_no || '—'}</TableCell>
                                  <TableCell>{formatDate(item.mfg_date)}</TableCell>
                                  <TableCell>{formatDate(item.exp_date)}</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          No product line items
                        </Typography>
                      )}
                    </Grid>

                    {/* Remarks */}
                    {viewExport.remarks && (
                      <>
                        <Grid item xs={12}>
                          <Typography variant="subtitle1" sx={{ 
                            fontWeight: 500, 
                            color: 'primary.main',
                            mb: 2,
                            pb: 1,
                            mt: 2,
                            borderBottom: `2px solid ${alpha(theme.palette.primary.main, 0.2)}`
                          }}>
                            Additional Notes
                          </Typography>
                        </Grid>
                        <Grid item xs={12}>
                          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, display: 'block', mb: 0.5 }}>
                            Remarks
                          </Typography>
                          <Paper sx={{ p: 2, bgcolor: alpha(theme.palette.grey[100], 0.5) }}>
                            <Typography variant="body1">
                              {viewExport.remarks}
                            </Typography>
                          </Paper>
                        </Grid>
                      </>
                    )}

                    {/* Invoice/Document */}
                    {viewExport.invoice_file_name && (
                      <>
                        <Grid item xs={12}>
                          <Typography variant="subtitle1" sx={{ 
                            fontWeight: 500, 
                            color: 'primary.main',
                            mb: 2,
                            pb: 1,
                            mt: 2,
                            borderBottom: `2px solid ${alpha(theme.palette.primary.main, 0.2)}`
                          }}>
                            Invoice/Document
                          </Typography>
                        </Grid>
                        <Grid item xs={12}>
                          <Card sx={{ p: 2, bgcolor: alpha(theme.palette.primary.main, 0.05) }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                              <DescriptionIcon sx={{ color: 'primary.main', fontSize: 40 }} />
                              <Box sx={{ flex: 1 }}>
                                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                  {viewExport.invoice_file_name}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {viewExport.invoice_file_size ? `${(viewExport.invoice_file_size / 1024).toFixed(2)} KB` : ''}
                                </Typography>
                              </Box>
                              <Button
                                variant="contained"
                                size="small"
                                startIcon={<VisibilityIcon />}
                                onClick={() => viewInvoiceFile(viewExport.invoice_file_path, viewExport.invoice_file_name)}
                              >
                                View
                              </Button>
                            </Box>
                          </Card>
                        </Grid>
                      </>
                    )}

                    {/* Approval Information */}
                    {viewExport.status === 'approved' && viewExport.approved_by_signature && (
                      <>
                        <Grid item xs={12}>
                          <Typography variant="subtitle1" sx={{ 
                            fontWeight: 500, 
                            color: 'success.main',
                            mb: 2,
                            pb: 1,
                            mt: 2,
                            borderBottom: `2px solid ${alpha(theme.palette.success.main, 0.2)}`
                          }}>
                            Approval Information
                          </Typography>
                        </Grid>
                        <Grid item xs={12}>
                          <Card sx={{ 
                            p: 3, 
                            border: `1px solid ${theme.palette.success.main}`, 
                            borderRadius: 2, 
                            backgroundColor: alpha(theme.palette.success.main, 0.05)
                          }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 500, mb: 2, color: 'success.main', display: 'flex', alignItems: 'center', gap: 1 }}>
                              <VerifiedIcon />
                              Approved By
                            </Typography>
                            <Box sx={{ 
                              border: `1px solid ${theme.palette.success.main}`, 
                              p: 2, 
                              borderRadius: 1, 
                              bgcolor: 'white',
                              mb: 2
                            }}>
                              <Box sx={{ 
                                fontFamily: "'Brush Script MT', 'Brush Script Std', cursive", 
                                fontSize: '24px', 
                                color: theme.palette.text.primary
                              }}>
                                {viewExport.approved_by_signature}
                              </Box>
                            </Box>
                            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                              Date: {formatDate(viewExport.approved_at)}
                            </Typography>
                            {viewExport.approval_notes && (
                              <Box sx={{ mt: 2 }}>
                                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, display: 'block', mb: 0.5 }}>
                                  Approval Notes:
                                </Typography>
                                <Typography variant="body2">{viewExport.approval_notes}</Typography>
                              </Box>
                            )}
                          </Card>
                        </Grid>
                      </>
                    )}
                  </Grid>
                )}
              </DialogContent>
              
              <DialogActions sx={{ p: 3, gap: 1 }}>
                <Button onClick={() => setViewDialogOpen(false)} variant="outlined">
                  Close
                </Button>
                {canEditExports() && (
                  <Button 
                    onClick={() => {
                      setViewDialogOpen(false);
                      handleOpenDialog(viewExport);
                    }}
                    variant="contained"
                    startIcon={<EditIcon />}
                  >
                    Edit Record
                  </Button>
                )}
              </DialogActions>
            </Dialog>

            {/* Approval Dialog */}
            <Dialog 
              open={approvalDialogOpen} 
              onClose={() => setApprovalDialogOpen(false)}
              maxWidth="sm"
              fullWidth
              disableScrollLock={true}
              PaperProps={{
                sx: {
                  borderRadius: 3,
                  border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                  boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.2)}`
                }
              }}
            >
              <DialogTitle sx={{ 
                fontWeight: 500,
                fontSize: '1.2rem',
                color: 'primary.main',
                borderBottom: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                background: alpha(theme.palette.primary.main, 0.05),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                p: 3
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <CheckCircleIcon sx={{ color: 'success.main' }} />
                  <Typography variant="h5" sx={{ fontWeight: 500, color: 'primary.main' }}>
                    Approve Export Record
                  </Typography>
                </Box>
                <IconButton 
                  onClick={() => setApprovalDialogOpen(false)} 
                  size="small"
                  sx={{
                    borderRadius: 2,
                    border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                    '&:hover': {
                      transform: 'scale(1.1)',
                      border: `1px solid ${theme.palette.primary.main}`,
                      boxShadow: `0 2px 8px ${alpha(theme.palette.grey[900], 0.2)}`
                    }
                  }}
                >
                  <CloseIcon />
                </IconButton>
              </DialogTitle>
              
              <DialogContent sx={{ 
                backgroundColor: alpha(theme.palette.grey[50], 0.5), 
                p: 3,
                maxHeight: '70vh', 
                overflowY: 'auto',
                mt: 4
              }}>
                {approvalExport && (
                  <Grid container spacing={3}>
                    <Grid item xs={12}>
                      <Card sx={{ 
                        p: 3, 
                        border: `1px solid ${theme.palette.grey[400]}`, 
                        borderRadius: 3, 
                        backgroundColor: 'white',
                        boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.08)}`
                      }}>
                        <Typography variant="h6" sx={{ fontWeight: 500, mb: 2, color: theme.palette.text.primary }}>
                          Approval Signature
                        </Typography>
                        <Grid container spacing={3}>
                          <Grid item xs={12}>
                            <TextField
                              fullWidth
                              label="Your Name (Electronic Signature) *"
                              value={signatureName}
                              onChange={(e) => setSignatureName(e.target.value)}
                              required
                              placeholder="Enter your full name to approve this record"
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  borderRadius: 2
                                }
                              }}
                            />
                          </Grid>
                          
                          {signatureName && (
                            <Grid item xs={12}>
                              <Box sx={{ 
                                border: `1px solid ${theme.palette.primary.main}`, 
                                p: 3, 
                                borderRadius: 2, 
                                bgcolor: alpha(theme.palette.primary.main, 0.05)
                              }}>
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1, fontWeight: 500 }}>
                                  ELECTRONIC SIGNATURE PREVIEW:
                                </Typography>
                                <Box sx={{ 
                                  fontFamily: "'Brush Script MT', 'Brush Script Std', cursive", 
                                  fontSize: '24px', 
                                  color: theme.palette.text.primary,
                                  textShadow: '1px 1px 1px rgba(0,0,0,0.1)',
                                  mb: 1
                                }}>
                                  {signatureName}
                                </Box>
                                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                                  Signed: {dayjs().format('MMM DD, YYYY HH:mm')}
                                </Typography>
                              </Box>
                            </Grid>
                          )}

                          <Grid item xs={12}>
                            <TextField
                              fullWidth
                              multiline
                              rows={3}
                              label="Approval Notes (Optional)"
                              value={approvalNotes}
                              onChange={(e) => setApprovalNotes(e.target.value)}
                              placeholder="Add any notes or comments about this approval..."
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  borderRadius: 2
                                }
                              }}
                            />
                          </Grid>
                        </Grid>
                      </Card>
                    </Grid>
                  </Grid>
                )}
              </DialogContent>
              
              <DialogActions sx={{ 
                p: 3, 
                gap: 2, 
                borderTop: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.primary.main, 0.04)})`
              }}>
                <Button 
                  onClick={() => setApprovalDialogOpen(false)}
                  startIcon={<CloseIcon />}
                  sx={{
                    borderRadius: 2,
                    px: 3,
                    py: 1.5,
                    color: theme.palette.text.secondary,
                    fontWeight: 500,
                    background: 'rgba(255, 255, 255, 0.8)',
                    backdropFilter: 'blur(10px)',
                    '&:hover': {
                      backgroundColor: alpha(theme.palette.primary.main, 0.04)
                    }
                  }}
                >
                  Cancel
                </Button>
                
                <Button 
                  onClick={handleApprovalSubmit}
                  variant="contained"
                  startIcon={<CheckCircleIcon />}
                  disabled={!signatureName.trim()}
                  sx={{
                    borderRadius: 2,
                    px: 4,
                    py: 1.5,
                    background: `linear-gradient(135deg, ${theme.palette.success.main}, ${theme.palette.success.dark})`,
                    color: 'white',
                    fontWeight: 500,
                    boxShadow: `0 4px 12px ${alpha(theme.palette.success.main, 0.3)}`,
                    '&:hover': {
                      background: `linear-gradient(135deg, ${theme.palette.success.dark}, ${theme.palette.success.main})`,
                      boxShadow: `0 6px 20px ${alpha(theme.palette.success.main, 0.4)}`
                    },
                    '&:disabled': {
                      background: theme.palette.grey[400],
                      color: theme.palette.grey[100]
                    }
                  }}
                >
                  Approve
                </Button>
              </DialogActions>
            </Dialog>

            {/* PI (Proforma Invoice) List Selection Dialog */}
            <Dialog 
              open={poListDialogOpen} 
              onClose={() => setPoListDialogOpen(false)}
              maxWidth="lg"
              fullWidth
              PaperProps={{
                sx: {
                  height: '95vh',
                  maxHeight: '95vh'
                }
              }}
            >
              <DialogTitle sx={{ 
                fontWeight: 500,
                color: 'primary.main',
                borderBottom: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexShrink: 0
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <SearchIcon />
                  <Typography variant="h6" sx={{ fontWeight: 500 }}>
                    Select Proforma Invoice
                  </Typography>
                </Box>
                <IconButton onClick={() => setPoListDialogOpen(false)} size="small">
                  <CloseIcon />
                </IconButton>
              </DialogTitle>
              
              <DialogContent sx={{ 
                mt: 2, 
                p: 3,
                display: 'flex', 
                flexDirection: 'column',
                overflow: 'hidden',
                flex: '1 1 auto',
                minHeight: 0
              }}>
                <Box sx={{ mb: 3, flexShrink: 0 }}>
                  <TextField
                    placeholder="Search by PI number or customer name..."
                    value={poSearchTerm}
                    onChange={(e) => setPoSearchTerm(e.target.value)}
                    size="small"
                    fullWidth
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Box>

                {loadingPOs ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <LinearProgress sx={{ width: '50%' }} />
                  </Box>
                ) : (
                  <TableContainer component={Paper} sx={{ 
                    flex: '1 1 auto',
                    overflow: 'auto',
                    minHeight: 0,
                    maxHeight: '100%'
                  }}>
                    <Table stickyHeader size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 500, bgcolor: 'white', position: 'sticky', top: 0, zIndex: 10 }}>
                            PI Number
                          </TableCell>
                          <TableCell sx={{ fontWeight: 500, bgcolor: 'white', position: 'sticky', top: 0, zIndex: 10 }}>
                            Customer Name
                          </TableCell>
                          <TableCell sx={{ fontWeight: 500, bgcolor: 'white', position: 'sticky', top: 0, zIndex: 10 }}>
                            PI Date
                          </TableCell>
                          <TableCell sx={{ fontWeight: 500, bgcolor: 'white', position: 'sticky', top: 0, zIndex: 10 }}>
                            Status
                          </TableCell>
                          <TableCell align="center" sx={{ fontWeight: 500, bgcolor: 'white', position: 'sticky', top: 0, zIndex: 10 }}>
                            Action
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {availablePOs
                          .filter(po => 
                            !poSearchTerm.trim() ||
                            po.po_number?.toLowerCase().includes(poSearchTerm.toLowerCase()) ||
                            po.customer_name?.toLowerCase().includes(poSearchTerm.toLowerCase())
                          )
                          .map((po) => (
                            <TableRow 
                              key={po.id}
                              hover
                              sx={{ 
                                cursor: 'pointer',
                                '&:hover': {
                                  bgcolor: alpha(theme.palette.primary.main, 0.05)
                                }
                              }}
                              onClick={() => handleSelectPO(po.po_number)}
                            >
                              <TableCell>
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                  {po.po_number}
                                </Typography>
                              </TableCell>
                              <TableCell>{po.customer_name || '—'}</TableCell>
                              <TableCell>{formatDate(po.po_date)}</TableCell>
                              <TableCell>
                                <Chip 
                                  label={po.po_status?.replace(/_/g, ' ').toUpperCase()} 
                                  size="small" 
                                  color="success"
                                />
                              </TableCell>
                              <TableCell align="center">
                                <Button
                                  size="small"
                                  variant="contained"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleSelectPO(po.po_number);
                                  }}
                                >
                                  Select
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                        {availablePOs.filter(po => 
                          !poSearchTerm.trim() ||
                          po.po_number?.toLowerCase().includes(poSearchTerm.toLowerCase()) ||
                          po.customer_name?.toLowerCase().includes(poSearchTerm.toLowerCase())
                        ).length === 0 && (
                          <TableRow>
                            <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                              <Typography variant="body2" color="text.secondary">
                                {poSearchTerm.trim() ? 'No PIs found matching your search' : 'No confirmed PIs available'}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </DialogContent>
              
              <DialogActions sx={{ p: 2, flexShrink: 0 }}>
                <Button onClick={() => setPoListDialogOpen(false)} variant="outlined">
                  Cancel
                </Button>
              </DialogActions>
            </Dialog>

            {/* Invoice Select Dialog */}
            <Dialog
              open={invoiceSelectDialogOpen}
              onClose={() => setInvoiceSelectDialogOpen(false)}
              maxWidth="lg"
              fullWidth
              PaperProps={{
                sx: {
                  height: '95vh',
                  maxHeight: '95vh'
                }
              }}
            >
              <DialogTitle sx={{
                fontWeight: 500,
                color: 'primary.main',
                borderBottom: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexShrink: 0
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <DescriptionIcon />
                  <Typography variant="h6" sx={{ fontWeight: 500 }}>
                    Select Invoice
                  </Typography>
                </Box>
                <IconButton onClick={() => setInvoiceSelectDialogOpen(false)} size="small">
                  <CloseIcon />
                </IconButton>
              </DialogTitle>

              <DialogContent sx={{
                mt: 2,
                p: 3,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                flex: '1 1 auto',
                minHeight: 0
              }}>
                <Box sx={{ mb: 3, flexShrink: 0 }}>
                  <TextField
                    placeholder="Search by invoice number or customer name..."
                    value={invoiceSearchTerm}
                    onChange={(e) => setInvoiceSearchTerm(e.target.value)}
                    size="small"
                    fullWidth
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Box>

                {loadingInvoices ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <LinearProgress sx={{ width: '50%' }} />
                  </Box>
                ) : (
                  <TableContainer component={Paper} sx={{
                    flex: '1 1 auto',
                    overflow: 'auto',
                    minHeight: 0,
                    maxHeight: '100%'
                  }}>
                    <Table stickyHeader size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 500, bgcolor: 'white', position: 'sticky', top: 0, zIndex: 10 }}>Invoice Number</TableCell>
                          <TableCell sx={{ fontWeight: 500, bgcolor: 'white', position: 'sticky', top: 0, zIndex: 10 }}>Customer Name</TableCell>
                          <TableCell sx={{ fontWeight: 500, bgcolor: 'white', position: 'sticky', top: 0, zIndex: 10 }}>Invoice Date</TableCell>
                          <TableCell sx={{ fontWeight: 500, bgcolor: 'white', position: 'sticky', top: 0, zIndex: 10 }}>Status</TableCell>
                          <TableCell align="center" sx={{ fontWeight: 500, bgcolor: 'white', position: 'sticky', top: 0, zIndex: 10 }}>Action</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {availableInvoices
                          .filter(inv =>
                            !invoiceSearchTerm.trim() ||
                            inv.invoice_number?.toLowerCase().includes(invoiceSearchTerm.toLowerCase()) ||
                            inv.customer_name?.toLowerCase().includes(invoiceSearchTerm.toLowerCase())
                          )
                          .map((inv) => (
                            <TableRow
                              key={inv.id}
                              hover
                              sx={{
                                cursor: 'pointer',
                                '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.05) }
                              }}
                              onClick={() => handleSelectInvoiceRecord(inv.id)}
                            >
                              <TableCell>
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                  {inv.invoice_number || '—'}
                                </Typography>
                              </TableCell>
                              <TableCell>{inv.customer_name || '—'}</TableCell>
                              <TableCell>{formatDate(inv.invoice_date)}</TableCell>
                              <TableCell>
                                <Chip
                                  label={inv.status?.replace(/_/g, ' ').toUpperCase() || 'N/A'}
                                  size="small"
                                  color={inv.status === 'paid' ? 'success' : inv.status === 'overdue' ? 'error' : 'default'}
                                />
                              </TableCell>
                              <TableCell align="center">
                                <Button
                                  size="small"
                                  variant="contained"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleSelectInvoiceRecord(inv.id);
                                  }}
                                >
                                  Select
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                        {availableInvoices.filter(inv =>
                          !invoiceSearchTerm.trim() ||
                          inv.invoice_number?.toLowerCase().includes(invoiceSearchTerm.toLowerCase()) ||
                          inv.customer_name?.toLowerCase().includes(invoiceSearchTerm.toLowerCase())
                        ).length === 0 && (
                          <TableRow>
                            <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                              <Typography variant="body2" color="text.secondary">
                                {invoiceSearchTerm.trim() ? 'No invoices found matching your search' : 'No invoices available'}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </DialogContent>

              <DialogActions sx={{ p: 2, flexShrink: 0 }}>
                <Button onClick={() => setInvoiceSelectDialogOpen(false)} variant="outlined">
                  Cancel
                </Button>
              </DialogActions>
            </Dialog>

            {/* Snackbar */}
            <Snackbar
              open={snackbar.open}
              autoHideDuration={4000}
              onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
              <Alert severity={snackbar.severity} onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}>
                {snackbar.message}
              </Alert>
            </Snackbar>
          </Container>
        </Box>
      </Fade>
    </LocalizationProvider>
  );
};

export default ExportsPage;